import { QueryClient } from '@tanstack/react-query';
import type { GetServerSideProps } from 'next';
import { storeLocale } from '@/root/constants';
import { CacheLong, CacheNone } from '@/root/strategies';
import { BlogLandingContent } from 'components/blogLandingContent/BlogLandingContent';
import { BlogLandingProvider } from 'components/blogLandingProvider/BlogLandingProvider';
import { BlogLandingNavigation } from 'components/blogNavigationsConnected/BlogNavigationsConnected';
import { BlogLandingHeader } from 'components/blogsHeaders/BlogsHeaders';
import { getAlternateUrls } from 'components/route/products/getAlternateUrls';
import type { GetBlogLandingQuery } from 'lib/contentful/__generated__/BlogLandingQuery';
import { useGetBlogLandingQuery } from 'lib/contentful/__generated__/BlogLandingQuery';
import { contentfulQueryDataSource } from 'lib/contentful/dataSources';
import type { DefaultProps } from 'utils/defaultsStaticProps';
import { defaultStaticProps } from 'utils/defaultsStaticProps';

export default function BlogLandingPage() {
  return (
    <BlogLandingProvider>
      <BlogLandingHeader />
      <BlogLandingNavigation />
      <BlogLandingContent />
    </BlogLandingProvider>
  );
}

export const getServerSideProps: GetServerSideProps<DefaultProps> = async (
  context
) => {
  const { res, preview = false } = context;

  if (preview) {
    res.setHeader('Cache-Control', CacheNone());
  } else {
    res.setHeader('Cache-Control', CacheLong());
  }

  const locale = storeLocale(context.locale);

  const contentfulBlogLandingQueryVariables = {
    handle: 'blogs',
    locale,
    preview: preview,
  };

  const queryClient = new QueryClient();
  const [staticProps, alternateUrls] = await Promise.all([
    defaultStaticProps(context, {
      queryClient,
      queries: [
        queryClient.prefetchQuery(
          useGetBlogLandingQuery.getKey(contentfulBlogLandingQueryVariables),
          useGetBlogLandingQuery.fetcher(
            contentfulQueryDataSource({ byPassCache: preview }),
            contentfulBlogLandingQueryVariables
          )
        ),
      ],
    }),
    getAlternateUrls({
      handle: 'blogs',
      basePath: '',
      contentType: 'blogs',
      locale,
      overrideHandle: 'blog',
      fieldToVerifyLocalisation: 'hero',
      localeToFilter: 'en-US',
    }),
  ]);

  const blogLandingData = queryClient
    .getQueryCache()
    .find<GetBlogLandingQuery>(
      useGetBlogLandingQuery.getKey(contentfulBlogLandingQueryVariables)
    );
  // Blog landing page should only be accessible if the current locale is present in the tags
  const tags =
    blogLandingData?.state.data?.blogs?.data[0]?.contentfulMetadata.tags;

  const isCurrentLocaleInTags = tags?.some(
    (tag) => tag?.id?.toLowerCase() === locale.toLowerCase()
  );

  if (!isCurrentLocaleInTags) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ...staticProps,
      alternateUrls,
    },
  };
};
